import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, trackerApi, type Board, type Ticket } from '../api/client'

/**
 * Доска экрана. Данные приходят long-poll'ом: сервер держит ответ, пока касса не
 * пришлёт новую ревизию снимка, поэтому изменение видно почти сразу и без опроса
 * вхолостую. Таймеры и цвет карточек считаются локально — на это запросы не нужны.
 */

/** Норматив приготовления по умолчанию, если у позиции он не задан. */
const DEFAULT_PREP_MIN = 15

function fmtElapsed(createdAt: number, now: number): string {
  const total = Math.max(0, Math.floor((now - createdAt) / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Цвет карточки: готово, просрочено вдвое, просрочено, норма. */
function cardClass(t: Ticket, now: number): string {
  if (t.status === 'ready') return 'card is-ready'
  const norm = t.prepTimeMinutes || DEFAULT_PREP_MIN
  const mins = (now - t.createdAt) / 60_000
  if (mins > norm * 2) return 'card is-late'
  if (mins > norm) return 'card is-warn'
  return 'card'
}

export function BoardPage({
  onUnauthorized
}: {
  onUnauthorized: (why: string) => void
}): React.JSX.Element {
  const [board, setBoard] = useState<Board | null>(null)
  const [netErr, setNetErr] = useState(false)
  const [now, setNow] = useState(Date.now())
  const [busyId, setBusyId] = useState<number | null>(null)
  // Тикеты держим отдельно: при tickets === null (ревизия не менялась) прежние
  // карточки остаются на доске и продолжают тикать.
  const ticketsRef = useRef<Ticket[]>([])

  // Локальные таймеры и цвет — без обращений к серверу.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const poll = useCallback(async (rev: number, signal: AbortSignal): Promise<number> => {
    const res = await trackerApi.board(rev, signal)
    if (res.tickets != null) ticketsRef.current = res.tickets
    setBoard(res)
    setNetErr(false)
    return res.rev
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    let alive = true
    let rev = 0
    const loop = async (): Promise<void> => {
      while (alive) {
        try {
          rev = await poll(rev, controller.signal)
        } catch (e) {
          if (!alive) return
          if (e instanceof ApiError && e.status === 401) {
            onUnauthorized(e.message)
            return
          }
          // Сеть отвалилась — показываем это и пробуем снова; доска остаётся.
          setNetErr(true)
          await new Promise((r) => setTimeout(r, 3000))
        }
      }
    }
    void loop()
    return () => {
      alive = false
      controller.abort()
    }
  }, [poll, onUnauthorized])

  const act = async (t: Ticket, status: Ticket['status']): Promise<void> => {
    setBusyId(t.id)
    try {
      await trackerApi.setStatus(t.id, status)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onUnauthorized(e.message)
    } finally {
      setBusyId(null)
    }
  }

  const deliver = async (t: Ticket): Promise<void> => {
    setBusyId(t.id)
    try {
      await trackerApi.deliver(t.orderId)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onUnauthorized(e.message)
    } finally {
      setBusyId(null)
    }
  }

  const live = ticketsRef.current.filter((t) => t.status !== 'served')
  const connLabel = netErr
    ? 'нет связи с сервером'
    : board?.online
      ? 'на связи'
      : board?.updatedAt
        ? `касса не на связи · данные от ${new Date(board.updatedAt).toLocaleTimeString('ru-RU')}`
        : 'касса не на связи'

  return (
    <>
      <div className="head">
        <h1>
          {board?.section === 'bar' ? 'Бар' : 'Кухня'}
          {board?.clubName ? ` · ${board.clubName}` : ''}
          {board?.deviceName ? ` · ${board.deviceName}` : ''}
        </h1>
        <span className={`conn ${netErr || !board?.online ? 'is-off' : 'is-on'}`}>{connLabel}</span>
      </div>

      {live.length === 0 ? (
        <div className="empty">Заказов нет</div>
      ) : (
        <div className="board">
          {live.map((t) => (
            <div key={t.id} className={cardClass(t, now)}>
              <div className="name">
                {t.name} <span className="qty">×{t.qty}</span>
              </div>
              {t.portion && <div className="portion">{t.portion}</div>}
              <div className="meta">
                <span>{t.tableLabel || 'без стола'}</span>
                <span className="timer">{fmtElapsed(t.createdAt, now)}</span>
              </div>
              <div className="row">
                {t.status === 'queued' && (
                  <button
                    className="primary"
                    disabled={busyId === t.id}
                    onClick={() => void act(t, 'cooking')}
                  >
                    Готовлю
                  </button>
                )}
                {t.status === 'cooking' && (
                  <button
                    className="done"
                    disabled={busyId === t.id}
                    onClick={() => void act(t, 'ready')}
                  >
                    Готово
                  </button>
                )}
                {t.status === 'ready' && (
                  <button disabled={busyId === t.id} onClick={() => void act(t, 'served')}>
                    Отдано
                  </button>
                )}
                {/* Доставка — только когда весь заказ готов и экрану её включили. */}
                {board?.deliveryEnabled && t.orderFulfillment === 'ready' && (
                  <button
                    className="primary"
                    disabled={busyId === t.id}
                    onClick={() => void deliver(t)}
                  >
                    Передан в доставку
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
